// library imports
import React, { useEffect, useState } from 'react';
import { Checkbox, Button, Menu, Card, Dropdown, Row, Col, Divider, Tooltip } from 'antd';
import styled from 'styled-components/macro';

// components
import CreateGlossaryEntityModal from '../entity/shared/EntityDropdown/CreateGlossaryEntityModal';
import BulkLoadEntityModal from '../entity/shared/EntityDropdown/BulkLoadEntityModal';
import { ComponentSpinner } from '../shared/ComponentSpinner';
import EmptyGlossarySection from './EmptyGlossarySection';
import GlossaryEntitiesPath from './GlossaryEntitiesPath';
import GlossaryEntitiesList from './GlossaryEntitiesList';
import { Notification } from '../shared/Notification';
import { Message } from '../shared/Message';
import ViewRelationshipsModal from './ManageRelationshipsModal';

// services
import { bulkDeleteGlossaryEntities } from '../entity/glossaryNode/utils/bulkDeleteGlossaryEntities';
import { useGlossaryEntityData } from '../entity/shared/GlossaryEntityContext';
import { sortGlossaryTerms } from '../entity/glossaryTerm/utils';
import { sortGlossaryNodes } from '../entity/glossaryNode/utils';
import { useTranslation } from '../../i18n/useTranslation';
import { useEntityRegistry } from '../useEntityRegistry';
import { usePagination } from '../shared/usePagination';

import { buildGlossaryBulkSelectHandlers, buildGlossaryExportInput, resolveGlossaryBulkActionStates } from './utils';
import { ENTITY_ACTIONS } from '../entity/shared/rbacConstantsService';
import { useExportEntityMutation, useBatchRemoveEntitiesMutation } from '../../graphql/mutations.generated';
import { getPermissionTooltips } from '../entity/shared/rbacUtilsService';
import { EntityType, EntityImportValidationMetadataInput } from '../../types.generated';
import { useRbac } from '../entity/shared/useRbac';
import {
    useGetRootGlossaryNodesQuery,
    useGetRootGlossaryTermsQuery,
    useGetEntityImportValidationMetadataQuery,
} from '../../graphql/glossary.generated';

import {
    BUSINESS_GLOSSARY_CREATE_TERM_ID,
    BUSINESS_GLOSSARY_CREATE_TERM_GROUP_ID,
} from '../onboarding/config/BusinessGlossaryOnboardingConfig';

// svg imports
import { ReactComponent as ExportIcon } from '../../images/icon-export.svg';
import { ReactComponent as PlusOutlined } from '../../images/add-outline.svg';
import { ReactComponent as UploadOutlined } from '../../images/upload-file.svg';
import { ReactComponent as ArrowDownIcon } from '../../images/keyboard_arrow_down.svg';
import { ReactComponent as DeleteOutlined } from '../../images/delete-outline.svg';

const GlossaryWrapper = styled(Card)`
    flex: 1;

    .f-entities-wrapper {
        padding: 0;

        .f-glossary-list-item {
            margin: 0;
            padding: 16px 12px;
        }
    }
`;

export const MAX_BROWSER_WIDTH = 500;
export const MIN_BROWSWER_WIDTH = 200;

const input: EntityImportValidationMetadataInput = {
    entityType: 'glossaryTerm',
};

function BusinessGlossaryPage() {
    const { t } = useTranslation();
    const { canPerformEntityMenuAction, platformPrivileges } = useRbac();

    const canViewTerms = !!platformPrivileges?.viewGlossaryTerm || !!platformPrivileges?.manageGlossaryTerms;
    const canViewNodes = !!platformPrivileges?.viewGlossaryNode || !!platformPrivileges?.manageGlossaryNodes;

    const {
        data: termsData,
        error: termsError,
        loading: termsLoading,
        refetch: refetchForTerms,
    } = useGetRootGlossaryTermsQuery({
        errorPolicy: 'all',
        skip: !canViewTerms,
    });

    const { data: headers } = useGetEntityImportValidationMetadataQuery({ variables: { input } });

    const {
        data: nodesData,
        error: nodesError,
        loading: nodesLoading,
        refetch: refetchForNodes,
    } = useGetRootGlossaryNodesQuery({
        errorPolicy: 'all',
        skip: !canViewNodes,
    });

    const entityRegistry = useEntityRegistry();
    const { setEntityData } = useGlossaryEntityData();

    useEffect(() => {
        setEntityData(null);
    }, [setEntityData]);

    const terms = termsData?.getRootGlossaryTerms?.terms.sort((termA, termB) =>
        sortGlossaryTerms(entityRegistry, termA, termB),
    );
    const nodes = nodesData?.getRootGlossaryNodes?.nodes.sort((nodeA, nodeB) =>
        sortGlossaryNodes(entityRegistry, nodeA, nodeB),
    );

    const hasTermsOrNodes = !!nodes?.length || !!terms?.length;

    const [isCreateTermModalVisible, setIsCreateTermModalVisible] = useState(false);
    const [isCreateNodeModalVisible, setIsCreateNodeModalVisible] = useState(false);
    const [isBulkLoadModalVisible, setIsBulkLoadModalVisible] = useState(false);
    const [isViewRelationshipsModalVisible, setIsViewRelationshipsModalVisible] = useState(false);
    const [isCsvExporting, setIsCsvExporting] = useState<boolean>(false);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [exportEntity, setExportEntity]: any = useState(null);
    const glossaries = [...(nodes || []), ...(terms || [])];

    const { page, pageSize, selectedItems, setSelectedItems, handleChangePage, handleShowSizeChange } = usePagination({
        items: glossaries.map((glossary) => glossary.urn || ''),
    });

    const [exportEntityMutation] = useExportEntityMutation();
    const [batchRemoveGlossaryMutation] = useBatchRemoveEntitiesMutation();

    const canCreateTerm = canPerformEntityMenuAction(EntityType.GlossaryTerm, ENTITY_ACTIONS.ADD_TERM);
    const canCreateNode = canPerformEntityMenuAction(EntityType.GlossaryNode, ENTITY_ACTIONS.ADD_TERM_GROUP);

    const canDeleteTerm = canPerformEntityMenuAction(EntityType.GlossaryTerm, ENTITY_ACTIONS.DELETE);
    const canDeleteNode = canPerformEntityMenuAction(EntityType.GlossaryNode, ENTITY_ACTIONS.DELETE);
    const canDelete = canDeleteTerm || canDeleteNode;

    const canExport = canPerformEntityMenuAction(EntityType.GlossaryTerm, ENTITY_ACTIONS.EXPORT);

    const canBulkImport = !!platformPrivileges?.bulkGlossaryImport;

    const { hasSelectedDeletableItems, hasSelectedExportableItems } = resolveGlossaryBulkActionStates({
        terms: terms || [],
        nodes: nodes || [],
        selectedItems,
        canDeleteTerm,
        canDeleteNode,
        canExport,
    });

    const { handleSelectAllSelectable, isAllSelectableSelected, isSelectableIndeterminate } =
        buildGlossaryBulkSelectHandlers({
            terms: terms || [],
            nodes: nodes || [],
            canInteractWithTerm: canDeleteTerm || canExport,
            canInteractWithNode: canDeleteNode || canExport,
            page,
            pageSize,
            selectedItems,
            setSelectedItems,
        });

    const tooltip = getPermissionTooltips(t, {
        export: canExport,
        delete: canDelete,
        createTerm: canCreateTerm,
        createNode: canCreateNode,
        bulkImport: canBulkImport,
        selectAll: canDelete || canExport,
    });

    /**
     * Handles the export of the selected glossary to a CSV file.
     * Calls the exportEntityMutation to export the selected glossary to a CSV file.
     * Displays a notification message to the user once the export is completed.
     * Clears the selectedItems state and sets the isAllSelected state to false.
     */
    const exportCsv = () => {
        setIsCsvExporting(true);
        setExportEntity(null);

        const exportInput = buildGlossaryExportInput(nodes || [], terms || [], selectedItems);

        exportEntityMutation({
            variables: {
                input: exportInput,
            },
        })
            .then((res = {}) => {
                const { data } = res;
                setExportEntity(data?.exportEntity);
                setSelectedItems([]);
            })
            .catch((e) => {
                if (e.networkError.statusCode === 503) {
                    const updatedInput = {
                        ...exportInput,
                        async: true,
                    };
                    // Retry with a different input for 504 errors
                    exportEntityMutation({
                        variables: {
                            input: updatedInput,
                        },
                    }).then((retryRes) => {
                        const { data } = retryRes;
                        setExportEntity(data?.exportEntity);
                        setSelectedItems([]);
                    });
                }
            })
            .finally(() => setIsCsvExporting(false));
    };

    /**
     * Handles the bulk deletion of the selected glossary terms.
     * Uses the reusable bulkDeleteGlossaryEntities utility.
     */
    const handleBulkDelete = () => {
        bulkDeleteGlossaryEntities({
            nodes: nodes || [],
            terms: terms || [],
            selectedItems,
            batchRemoveGlossaryMutation,
            setSelectedItems,
            refetch: () => {
                refetchForTerms();
                refetchForNodes();
                return Promise.resolve();
            },
            onSuccess: () => {
                handleChangePage(1);
            },
            setIsDeleting,
        });
    };

    const isLoading = termsLoading || nodesLoading || isCsvExporting || isDeleting;

    return (
        <>
            {exportEntity && <Notification exportEntity={exportEntity} />}

            <GlossaryWrapper className="f-mt-2-xs">
                {((termsError && !termsData) || (nodesError && !nodesData)) && (
                    <Message type="error" content={t('Failed to load glossary! An unexpected error occurred.')} />
                )}
                <GlossaryEntitiesPath />
                <Row className="px-3 py-2" align="middle" gutter={[8, 8]}>
                    <Col flex="none">
                        <Tooltip title={tooltip.export} overlayClassName="f-tooltip-white">
                            <Button
                                type="text"
                                onClick={exportCsv}
                                className="f-btn-flat"
                                disabled={!hasSelectedExportableItems}
                            >
                                <ExportIcon aria-hidden="true" />
                                <span>{t('Export')}</span>
                            </Button>
                        </Tooltip>
                    </Col>
                    <Col flex="none">
                        <Tooltip title={tooltip.delete} overlayClassName="f-tooltip-white">
                            <Button
                                type="text"
                                className="f-btn-flat"
                                onClick={handleBulkDelete}
                                disabled={!hasSelectedDeletableItems}
                            >
                                <DeleteOutlined aria-hidden="true" />
                                <span>{t('Delete')}</span>
                            </Button>
                        </Tooltip>
                    </Col>
                </Row>
                <Divider className="ma-0" />
                <Card
                    className="f-card-stack"
                    title={
                        <Row align="middle" gutter={[8, 8]}>
                            <Col flex="auto">
                                <Tooltip title={tooltip.selectAll} overlayClassName="f-tooltip-white">
                                    <Checkbox
                                        checked={isAllSelectableSelected()}
                                        indeterminate={isSelectableIndeterminate()}
                                        disabled={isLoading || (!canDelete && !canExport)}
                                        onChange={(e) => handleSelectAllSelectable(e.target.checked)}
                                    >
                                        {t('Select All')}
                                    </Checkbox>
                                </Tooltip>
                            </Col>
                            <Col flex="none">
                                <Dropdown
                                    overlay={
                                        <Menu>
                                            <Tooltip title={tooltip.bulkImport} overlayClassName="f-tooltip-white">
                                                <span>
                                                    <Menu.Item
                                                        key="1"
                                                        tabIndex={0}
                                                        icon={<UploadOutlined />}
                                                        aria-label={t('Bulk Load')}
                                                        disabled={!canBulkImport}
                                                        onClick={() => setIsBulkLoadModalVisible(true)}
                                                    >
                                                        <span>{t('Bulk Load')}</span>
                                                    </Menu.Item>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={tooltip.createTerm} overlayClassName="f-tooltip-white">
                                                <span>
                                                    <Menu.Item
                                                        key="2"
                                                        tabIndex={0}
                                                        icon={<PlusOutlined />}
                                                        aria-label={t('Add Term')}
                                                        data-testid="add-term-button"
                                                        disabled={!canCreateTerm}
                                                        id={BUSINESS_GLOSSARY_CREATE_TERM_ID}
                                                        onClick={() => setIsCreateTermModalVisible(true)}
                                                    >
                                                        <span>{t('Add Term')}</span>
                                                    </Menu.Item>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title={tooltip.createNode} overlayClassName="f-tooltip-white">
                                                <span>
                                                    <Menu.Item
                                                        key="3"
                                                        tabIndex={0}
                                                        icon={<PlusOutlined />}
                                                        aria-label={t('Add Term Group')}
                                                        disabled={!canCreateNode}
                                                        data-testid="add-term-group-button"
                                                        id={BUSINESS_GLOSSARY_CREATE_TERM_GROUP_ID}
                                                        onClick={() => setIsCreateNodeModalVisible(true)}
                                                    >
                                                        <span>{t('Add Term Group')}</span>
                                                    </Menu.Item>
                                                </span>
                                            </Tooltip>
                                        </Menu>
                                    }
                                    trigger={['click']}
                                >
                                    <Button type="primary">
                                        <PlusOutlined aria-hidden="true" />
                                        <span>{t('Add')}</span>
                                        <ArrowDownIcon aria-hidden="true" />
                                    </Button>
                                </Dropdown>
                            </Col>
                            <Col flex="none">
                                <Button type="default" onClick={() => setIsViewRelationshipsModalVisible(true)}>
                                    {t('Manage Relationships')}
                                </Button>
                            </Col>
                        </Row>
                    }
                >
                    {isLoading && <ComponentSpinner />}

                    {hasTermsOrNodes && (
                        <GlossaryEntitiesList
                            page={page}
                            nodes={nodes || []}
                            terms={terms || []}
                            pageSize={pageSize}
                            selectedItems={selectedItems}
                            canExport={canExport}
                            canDeleteTerm={canDeleteTerm}
                            canDeleteNode={canDeleteNode}
                            onChangePage={handleChangePage}
                            setSelectedItems={setSelectedItems}
                            onShowSizeChange={handleShowSizeChange}
                        />
                    )}
                    {!(termsLoading || nodesLoading) && !hasTermsOrNodes && (
                        <EmptyGlossarySection
                            title={t('Empty Glossary')}
                            refetchForTerms={refetchForTerms}
                            refetchForNodes={refetchForNodes}
                            description={t('CREATE_TERMS_TERM_GROUPS_ORGANIZE_DATA_DESC')}
                        />
                    )}
                </Card>
            </GlossaryWrapper>
            {isBulkLoadModalVisible && (
                <BulkLoadEntityModal
                    entityType={EntityType.GlossaryTerm}
                    onClose={() => setIsBulkLoadModalVisible(false)}
                    expectedHeadersFromApi={
                        headers?.getEntityImportValidationMetadata?.validationMetadata.map((header) => ({
                            ...header,
                            validate: header.validate === null ? undefined : header.validate,
                            sampleRows: header.sampleRows.map((row) => row.map((cell) => cell || '')),
                        })) || []
                    }
                />
            )}
            {isCreateTermModalVisible && (
                <CreateGlossaryEntityModal
                    entityType={EntityType.GlossaryTerm}
                    onClose={() => setIsCreateTermModalVisible(false)}
                    refetchData={refetchForTerms}
                />
            )}
            {isCreateNodeModalVisible && (
                <CreateGlossaryEntityModal
                    entityType={EntityType.GlossaryNode}
                    onClose={() => setIsCreateNodeModalVisible(false)}
                    refetchData={refetchForNodes}
                />
            )}
            {isViewRelationshipsModalVisible && (
                <ViewRelationshipsModal onClose={() => setIsViewRelationshipsModalVisible(false)} />
            )}
        </>
    );
}

export default BusinessGlossaryPage;
